import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { RequestStatus } from '@freelancer/datastore';
import {
  ProjectUpgradeCardOption,
  ProjectUpgradeFees,
  ProjectUpgradeFeesCollection,
} from '@freelancer/datastore/collections';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { HeadingColor, HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import {
  FontColor,
  FontType,
  FontWeight,
  TextSize,
  TextTransform,
} from '@freelancer/ui/text';
import * as Rx from 'rxjs';
import { distinctUntilChanged, map, startWith, tap } from 'rxjs/operators';

export interface ProjectAdvancedUpgrades {
  featured: boolean;
  nda: boolean;
  private: boolean;
  recruiter: boolean;
  urgent: boolean;
  ipContract: boolean;
  sealed: boolean;
}

@Component({
  selector: 'app-project-upgrades',
  template: `
    <fl-bit *ngIf="showFreeRecruiterUpgradeBanner">
      <fl-bit class="UpgradesFieldLabel">
        <fl-heading
          i18n="Post Job Page local project upgrade field label"
          [flMarginBottom]="Margin.XSMALL"
          [flMarginRight]="Margin.XXXSMALL"
          [headingType]="HeadingType.H3"
          [size]="TextSize.MID"
        >
          Upgrades
          <fl-text
            [color]="FontColor.MID"
            [flMarginBottom]="Margin.XSMALL"
            [fontType]="FontType.SPAN"
            [size]="TextSize.XXXSMALL"
            [textTransform]="TextTransform.UPPERCASE"
            [weight]="FontWeight.BOLD"
          >
            Optional
          </fl-text>
        </fl-heading>
      </fl-bit>
      <fl-banner-alert
        i18n="Free Recruiter Upgrade Banner Label"
        [closeable]="false"
        [flMarginBottom]="Margin.XSMALL"
        [type]="BannerAlertType.INFO"
      >
        Your standard project comes with a free Recruiter upgrade.
      </fl-banner-alert>
    </fl-bit>
    <fl-bit *ngIf="!showFreeRecruiterUpgradeBanner">
      <fl-heading
        i18n="Post Job Page project upgrade field label"
        [headingType]="HeadingType.H3"
        [flMarginBottom]="Margin.XXXSMALL"
        [size]="TextSize.MID"
      >
        Do you need a helping hand?
      </fl-heading>
    </fl-bit>
    <fl-bit [flMarginBottom]="Margin.SMALL">
      <fl-grid [vAlign]="VerticalAlignment.VERTICAL_STRETCH">
        <fl-col
          [col]="12"
          [colTablet]="6"
          [flMarginBottom]="Margin.SMALL"
          [flMarginBottomTablet]="Margin.NONE"
        >
          <fl-bit
            class="ProjectUpgrade-card"
            [ngClass]="{ IsDisabled: showFreeRecruiterUpgradeBanner }"
          >
            <fl-radio-card
              flTrackingLabel="Standard"
              i18n-pictureAlt="
                 Post Job Page project upgrade - icon for standard upgrade
              "
              pictureAlt="Standard Project"
              pictureSrc="job-post/project-icon.svg"
              [control]="projectTypeControl"
              [isSelected]="
                projectTypeControl.value === ProjectUpgradeCardOption.STANDARD
              "
              [value]="ProjectUpgradeCardOption.STANDARD"
              (click)="handleCardSelected(ProjectUpgradeCardOption.STANDARD)"
            >
              <fl-radio-card-body>
                <fl-text
                  i18n="Post Job Page project upgrade - choose standard project"
                  [size]="TextSize.SMALL"
                  [weight]="FontWeight.BOLD"
                >
                  Standard project
                </fl-text>

                <fl-text
                  i18n="
                     Post Job Page project upgrade - standard project
                    description
                  "
                  [flMarginBottom]="Margin.SMALL"
                >
                  Free to post, your project will go live instantly and start
                  receiving bids within seconds.
                </fl-text>
                <fl-text
                  i18n="Post Job Page project upgrade -standard project price"
                  [weight]="FontWeight.BOLD"
                >
                  FREE
                </fl-text>
              </fl-radio-card-body>
            </fl-radio-card>
          </fl-bit>
        </fl-col>
        <fl-col
          class="ProjectUpgrade-card"
          [col]="12"
          [colTablet]="6"
          [ngClass]="{ IsDisabled: upgradeFeesStatus?.error }"
        >
          <fl-radio-card
            *ngIf="showFreeRecruiterUpgradeBanner"
            flTrackingLabel="Recruiter"
            i18n-pictureAlt="
               Post Job Page project upgrade - icon for recruiter upgrade
            "
            pictureAlt="Recruiter Project"
            pictureSrc="job-post/recruiter-001.svg"
            [control]="projectTypeControl"
            [isSelected]="true"
          >
            <fl-radio-card-body>
              <fl-text
                i18n="
                   Post Job Page project upgrade - choose recruiter upgrade
                "
                [size]="TextSize.SMALL"
                [weight]="FontWeight.BOLD"
              >
                Recruiter project
              </fl-text>
              <fl-text
                i18n="
                   Post Job Page project upgrade - recruiter upgrade description

                "
                [flMarginBottom]="Margin.SMALL"
              >
                We'll assign one of our expert staff to help you find the
                perfect freelancer for the job.
              </fl-text>
              <ng-container>
                <fl-bit class="ProjectUpgrade-price-container">
                  <fl-text
                    i18n="
                       Post Job Page project upgrade - recruiter upgrade price
                    "
                    [color]="FontColor.PROMO"
                    [flMarginRight]="Margin.XXXSMALL"
                    [fontType]="FontType.SPAN"
                    [weight]="FontWeight.BOLD"
                  >
                    FREE
                  </fl-text>
                  <fl-text
                    *ngIf="
                      upgradeFees &&
                      upgradeFeesStatus &&
                      upgradeFeesStatus.ready &&
                      !upgradeFeesStatus.error
                    "
                    class="ProjectUpgrade-price-full"
                    [weight]="FontWeight.BOLD"
                    [fontType]="FontType.SPAN"
                  >
                    {{
                      upgradeFees.assistedFullPrice
                        | flCurrency: upgradeFees.currency.code
                    }}
                  </fl-text>
                </fl-bit>
              </ng-container>
            </fl-radio-card-body>
          </fl-radio-card>
          <fl-radio-card
            *ngIf="!showFreeRecruiterUpgradeBanner"
            flTrackingLabel="Recruiter"
            i18n-pictureAlt="
               Post Job Page project upgrade - icon for recruiter upgrade
            "
            pictureAlt="Recruiter Project"
            pictureSrc="job-post/recruiter-001.svg"
            [control]="projectTypeControl"
            [isSelected]="isRecruiterCardSelected$ | async"
            [value]="ProjectUpgradeCardOption.RECRUITER"
            (click)="handleCardSelected(ProjectUpgradeCardOption.RECRUITER)"
          >
            <fl-radio-card-body>
              <fl-ribbon
                i18n="
                   Post Job Page project upgrade - choose recruiter upgrade
                  recommended ribbon
                "
              >
                Recommended
              </fl-ribbon>
              <fl-text
                i18n="Post Job Page project upgrade - choose recruiter upgrade"
                [size]="TextSize.SMALL"
                [weight]="FontWeight.BOLD"
              >
                Recruiter project
              </fl-text>
              <fl-text
                i18n="
                   Post Job Page project upgrade - recruiter upgrade description

                "
                [flMarginBottom]="Margin.SMALL"
              >
                We'll assign one of our expert staff to help you find the
                perfect freelancer for the job.
              </fl-text>
              <ng-container
                *ngIf="
                  upgradeFees &&
                    upgradeFeesStatus &&
                    upgradeFeesStatus.ready &&
                    !upgradeFeesStatus.error;
                  else loadingRecruiterPrice
                "
              >
                <fl-bit class="ProjectUpgrade-price-container">
                  <fl-text
                    [color]="FontColor.PROMO"
                    [flMarginRight]="Margin.XXXSMALL"
                    [fontType]="FontType.SPAN"
                    [weight]="FontWeight.BOLD"
                  >
                    <ng-container
                      *ngIf="upgradeFees?.freeAssisted"
                      i18n="
                         Post Job Page project upgrade - recruiter upgrade free
                      "
                    >
                      FREE
                    </ng-container>
                    <ng-container
                      *ngIf="!upgradeFees?.freeAssisted"
                      i18n="
                         Post Job Page project upgrade - recruiter upgrade promo
                        price
                      "
                    >
                      ONLY
                      {{
                        upgradeFees.assistedPrice
                          | flCurrency: upgradeFees.currency.code
                      }}
                    </ng-container>
                  </fl-text>
                  <fl-text
                    class="ProjectUpgrade-price-full"
                    [weight]="FontWeight.BOLD"
                    [fontType]="FontType.SPAN"
                  >
                    {{
                      upgradeFees.assistedFullPrice
                        | flCurrency: upgradeFees.currency.code
                    }}
                  </fl-text>
                </fl-bit>
              </ng-container>
              <ng-template #loadingRecruiterPrice>
                <fl-loading-text [rows]="1" [padded]="false"></fl-loading-text>
              </ng-template>
            </fl-radio-card-body>
          </fl-radio-card>
        </fl-col>
      </fl-grid>
    </fl-bit>
    <fl-text
      *ngIf="upgradeFeesStatus?.error"
      i18n="Post Job Page project upgrade fees update error message"
      [color]="FontColor.ERROR"
      [flMarginBottom]="Margin.LARGE"
    >
      Can't update fees at the moment. You can always upgrade after posting your
      project.
    </fl-text>
    <fl-bit [flMarginBottom]="Margin.LARGE" class="AdvancedOptions-link">
      <fl-link
        *ngIf="!showAdvanced"
        flTrackingLabel="ShowAdvanced"
        i18n="Post Job Page project upgrade - show advanced upgrade options"
        [flMarginRight]="Margin.NONE"
        (click)="showAdvancedOptions()"
      >
        Show advanced options
      </fl-link>
      <fl-link
        *ngIf="showAdvanced"
        flTrackingLabel="HideAdvanced"
        i18n="Post Job Page project upgrade - show advanced upgrade options"
        [flMarginRight]="Margin.NONE"
        (click)="hideAdvancedOptions()"
      >
        Hide advanced options
      </fl-link>
    </fl-bit>

    <app-project-advanced-upgrades
      *ngIf="showAdvanced"
      class="AdvancedOptions-content"
      [control]="control"
      [isLoadingFees]="
        upgradeFeesStatus &&
        (!upgradeFeesStatus.ready || upgradeFeesStatus.error)
      "
      [upgradeFees]="upgradeFees"
      [showRecruiterUpgrade]="!showFreeRecruiterUpgradeBanner"
    ></app-project-advanced-upgrades>
  `,
  styleUrls: ['./upgrades.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectUpgradesComponent implements OnInit, OnChanges, OnDestroy {
  BannerAlertType = BannerAlertType;
  FontColor = FontColor;
  TextSize = TextSize;
  TextTransform = TextTransform;
  FontType = FontType;
  FontWeight = FontWeight;
  HeadingColor = HeadingColor;
  HeadingType = HeadingType;
  Margin = Margin;
  ProjectUpgradeCardOption = ProjectUpgradeCardOption;
  VerticalAlignment = VerticalAlignment;

  @Input() control: FormControl;
  @Input() projectTypeControl: FormControl;
  @Input() showFreeRecruiterUpgradeBanner: boolean;
  @Input() upgradeFees: ProjectUpgradeFees;
  @Input() upgradeFeesStatus: RequestStatus<ProjectUpgradeFeesCollection>;

  @Output() cardSelected: EventEmitter<
    ProjectUpgradeCardOption
  > = new EventEmitter();
  @Output() advancedUpgradesClicked: EventEmitter<boolean> = new EventEmitter();

  showAdvanced = false;

  // Data stream
  isRecruiterCardSelected$: Rx.Observable<boolean>;
  isStandardCardSelected$: Rx.Observable<boolean>;
  toggleRecruiterCardSubscription?: Rx.Subscription;
  toggleRecruiterUpgradeSubscription?: Rx.Subscription;

  ngOnInit() {
    if (this.upgradeFeesStatus && this.upgradeFeesStatus.error) {
      this.disableUpgrades();
    }

    this.isStandardCardSelected$ = this.projectTypeControl.valueChanges.pipe(
      startWith(this.projectTypeControl.value),
      map(projectType => projectType === ProjectUpgradeCardOption.STANDARD),
    );

    this.isRecruiterCardSelected$ = this.projectTypeControl.valueChanges.pipe(
      startWith(this.projectTypeControl.value),
      map(projectType => projectType === ProjectUpgradeCardOption.RECRUITER),
    );

    this.toggleRecruiterCardSubscription = this.control.valueChanges
      .pipe(
        distinctUntilChanged(),
        tap((upgrades: ProjectAdvancedUpgrades) => {
          if (
            upgrades.recruiter &&
            this.projectTypeControl.value !== ProjectUpgradeCardOption.RECRUITER
          ) {
            this.projectTypeControl.setValue(
              ProjectUpgradeCardOption.RECRUITER,
            );
          }

          if (
            !upgrades.recruiter &&
            this.projectTypeControl.value === ProjectUpgradeCardOption.RECRUITER
          ) {
            this.projectTypeControl.setValue('');
          }
        }),
      )
      .subscribe();

    this.toggleRecruiterUpgradeSubscription = this.projectTypeControl.valueChanges
      .pipe(
        startWith(this.projectTypeControl.value),
        distinctUntilChanged(),
        tap((card: ProjectUpgradeCardOption) => {
          if (
            card === ProjectUpgradeCardOption.STANDARD &&
            this.control.value.recruiter
          ) {
            this.control.patchValue({ recruiter: false });
          }

          if (
            card === ProjectUpgradeCardOption.RECRUITER &&
            !this.control.value.recruiter
          ) {
            this.control.patchValue({ recruiter: true });
          }
        }),
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.upgradeFeesStatus?.currentValue) {
      if (
        changes.upgradeFeesStatus.currentValue.error &&
        this.control.enabled
      ) {
        this.disableUpgrades();
      } else if (
        !changes.upgradeFeesStatus.currentValue.error &&
        this.control.disabled
      ) {
        this.control.enable();
      }
    }

    if (
      changes.upgradeFees?.currentValue &&
      this.projectTypeControl.untouched
    ) {
      const fees = changes.upgradeFees.currentValue;
      if (
        fees.freeAssisted ||
        fees.freeNda ||
        fees.freeFeatured ||
        fees.freeUrgent ||
        fees.freePrivate ||
        fees.freeIpContract ||
        fees.freeSealed
      ) {
        this.showAdvancedOptions();
      }
    }
  }

  ngOnDestroy() {
    if (this.toggleRecruiterCardSubscription) {
      this.toggleRecruiterCardSubscription.unsubscribe();
    }
    if (this.toggleRecruiterUpgradeSubscription) {
      this.toggleRecruiterUpgradeSubscription.unsubscribe();
    }
  }

  showAdvancedOptions() {
    this.showAdvanced = true;
    this.advancedUpgradesClicked.emit(true);
  }

  hideAdvancedOptions() {
    this.showAdvanced = false;
    this.advancedUpgradesClicked.emit(false);
  }

  disableUpgrades() {
    this.resetAdvanced();
    this.control.disable();
  }

  resetAdvanced() {
    this.control.setValue({
      featured: false,
      nda: false,
      private: false,
      recruiter: false,
      urgent: false,
      sealed: false,
      ipContract: false,
    });
  }

  handleCardSelected(upgradeCardOption: ProjectUpgradeCardOption) {
    if (this.projectTypeControl.enabled) {
      this.projectTypeControl.markAsTouched();
      this.cardSelected.emit(upgradeCardOption);
    }
  }
}
